import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgClass } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { Address, Schedule, Zone } from '@domains';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ZoneService } from '@rspl-api';
import { Destructible, DisableDoubleClickDirective, FxLayoutAlignDirective, FxLayoutDirective, FxLayoutGapDirective } from '@rspl-ui';
import { of, take, takeUntil } from 'rxjs';

import { ZoneLookupComponent } from '../../../../../../ui/src/lib/lookup/zone-lookup/zone-lookup.component';
import { AppState } from '../../../store/app-state.interface';
import { updateTruckSchedule, updateTruckScheduleSuccess } from '../../../store/truck/truck.actions';
import { PreviewZoneChangeDialogComponent } from '../preview-zone-change-dialog/preview-zone-change-dialog.component';

export interface MultipleZoneData {
  schedule: Schedule;
  address: Address;
  parentId: string;
}

@Component({
  selector: 'app-multiple-zone-dialog',
  templateUrl: './multiple-zone-dialog.component.html',
  styleUrls: ['./multiple-zone-dialog.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    MatDialogActions,
    FxLayoutDirective,
    FxLayoutAlignDirective,
    MatButton,
    DisableDoubleClickDirective,
    ReactiveFormsModule,
    ZoneLookupComponent,
    MatIcon,
    FxLayoutDirective,
    FxLayoutGapDirective,
    MatCheckbox,
    FxLayoutAlignDirective,
    NgClass,
  ],
})
export class MultipleZoneDialogComponent extends Destructible implements OnInit {
  zone: Zone;
  days = {
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    sunday: true,
  };
  changes = {
    monday: { minus: 0, plus: 0, plusZips: [], minusZips: [], commonZips: [] },
    tuesday: { minus: 0, plus: 0, plusZips: [], minusZips: [], commonZips: [] },
    wednesday: { minus: 0, plus: 0, plusZips: [], minusZips: [], commonZips: [] },
    thursday: { minus: 0, plus: 0, plusZips: [], minusZips: [], commonZips: [] },
    friday: { minus: 0, plus: 0, plusZips: [], minusZips: [], commonZips: [] },
    saturday: { minus: 0, plus: 0, plusZips: [], minusZips: [], commonZips: [] },
    sunday: { minus: 0, plus: 0, plusZips: [], minusZips: [], commonZips: [] },
  };
  public constructor(
    private dialog: MatDialog,
    public reference: MatDialogRef<MultipleZoneDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MultipleZoneData,
    private zoneService: ZoneService,
    private store: Store<AppState>,
    private actions: Actions,
  ) {
    super();
  }

  ngOnInit(): void {
    this.actions.pipe(ofType(updateTruckScheduleSuccess), takeUntil(this.destroy$)).subscribe(() => {
      this.close();
    });
  }

  public close(): void {
    this.reference.close();
  }

  public updateSavedZones(): void {
    this.reference.close({ updateSavedZones: true });
  }

  get allSelected(): boolean {
    return (
      this.days.monday &&
      this.days.tuesday &&
      this.days.wednesday &&
      this.days.thursday &&
      this.days.friday &&
      this.days.saturday &&
      this.days.sunday
    );
  }

  get anySelected(): boolean {
    return (
      this.days.monday ||
      this.days.tuesday ||
      this.days.wednesday ||
      this.days.thursday ||
      this.days.friday ||
      this.days.saturday ||
      this.days.sunday
    );
  }

  toggleAll() {
    const value = !this.allSelected;
    this.days.monday = value;
    this.days.tuesday = value;
    this.days.wednesday = value;
    this.days.thursday = value;
    this.days.friday = value;
    this.days.saturday = value;
    this.days.sunday = value;
  }

  get listOfDays() {
    return Object.keys(this.days);
  }

  findDiff(
    scheduleZips: string[],
    zoneZips: string[],
  ): {
    plus: number;
    minus: number;
    plusZips: string[];
    minusZips: string[];
    commonZips: string[];
  } {
    const setScheduleZips = new Set(scheduleZips);
    const setZoneZips = new Set(zoneZips);

    const plusZips = [...setZoneZips].filter((x) => !setScheduleZips.has(x));
    const minusZips = [...setScheduleZips].filter((x) => !setZoneZips.has(x));
    const commonZips = [...setScheduleZips].filter((x) => setZoneZips.has(x));

    return {
      plus: plusZips.length,
      minus: minusZips.length,
      plusZips,
      minusZips,
      commonZips,
    };
  }

  selectZone(zone: Zone) {
    this.zone = zone;
    this.listOfDays.forEach((day) => {
      this.changes[day] = this.findDiff(this.data.schedule[day + 'Zone'].amZips, this.zone.zips);
    });
  }

  preview(change: { minus: 0; plus: 0; plusZips: []; minusZips: []; commonZips: [] }, day: string) {
    (this.data.schedule.meta[day + '_zone_id'] ? this.zoneService.find(this.data.schedule.meta[day + '_zone_id']) : of(null))
      .pipe(take(1))
      .subscribe((scheduleZone) => {
        this.dialog
          .open(PreviewZoneChangeDialogComponent, {
            width: '900px',
            maxWidth: '90vw',
            disableClose: true,
            data: {
              ...change,
              day,
              address: this.data.address,
              schedule: scheduleZone || new Zone({ zips: this.data.schedule[day + 'Zone'].amZips }),
              zone: this.zone,
            },
          })
          .afterClosed()
          .pipe(take(1))
          .subscribe((res) => {
            this.selectZone(res);
          });
      });
  }

  save(): void {
    this.listOfDays
      .filter((day) => this.days[day])
      .forEach((day) => {
        this.data.schedule[day + 'Zone'].amZips = this.zone.zips;
        this.data.schedule[day + 'Zone'].pmZips = this.zone.zips;
        this.data.schedule.meta[day + '_zone_id'] = this.zone.id;
      });

    this.store.dispatch(
      updateTruckSchedule({
        parentId: this.data.parentId,
        schedule: this.data.schedule,
      }),
    );
  }
}
